import { Heading } from "@/components/ui/Heading";
import { RadioGroup, RadioCard, RadioImage } from "@/components/ui/Radio";
import classes from "./CardSelector.module.scss";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import { Select } from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useDonationFormStore } from "@/stores";
import { CardSelectorAddressForm } from "./CardSelectorAddressForm";
import { useTranslation } from "@/hooks/useTranslation";
import type { CardSelectorProps } from "./CardSelectorProps";
import type {
  Celebration,
  CelebrationImage,
} from "@/types/components/CardSelectorComponent";
import { getAsset } from "@/utils/assets";
import { clsx } from "clsx";
import { useEffect } from "react";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { Field } from "@/components/Field";
import {
  type CardSelector as CardSelectorType,
  type CardType,
  type CardImage,
} from "@/stores";
import * as z from "zod";

const LIMIT_MESSAGE_CHARACTERS = 255;

export function CardSelector(props: CardSelectorProps) {
  const { config, className, ref } = props;

  const { donationOneOffType, cardSelector } = useDonationFormStore();

  const setFieldValue = useDonationFormStore((state) => state.setFieldValue);

  const { cardType, celebrationType } = cardSelector;

  const t = useTranslation("card_selector");

  const celebrationOptions = config["card-selector-celebrations"]
    .filter(
      (celebration: Celebration) =>
        celebration.enabled && celebration.purpose_key === donationOneOffType,
    )
    .map((celebration: Celebration) => ({
      value: celebration.value,
      label: t(celebration.label.i18n) as string,
      images: celebration.images,
    }));

  useEffect(() => {
    if (celebrationOptions.length === 0) {
      setFieldValue("cardSelector.celebrationType", "-");
    }

    if (celebrationOptions.length === 1) {
      setFieldValue(
        "cardSelector.celebrationType",
        celebrationOptions[0].value,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeCardType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cardType = event.target.value as CardType;
    setFieldValue("cardSelector.cardType", cardType);
  };

  const enableSelectCard = cardType === "e-card" || cardType === "post";

  const selectedCelebration = celebrationOptions.find((celebration) => {
    return celebration.value === celebrationType;
  });

  const cardImages = (selectedCelebration?.images || []) as CelebrationImage[];

  return (
    <ValidationGroupProvider name="cardSelector" ref={ref}>
      <div className={clsx(classes.wrapper, className)}>
        <Stack gap="lg">
          <FormControl type="fieldset" label={t("select-card-type")}>
            <RadioGroup
              name="cardType"
              value={cardType}
              layout="auto-fit"
              onChange={handleChangeCardType}
            >
              <RadioCard value="e-card" variant="secondary">
                <div className={classes.cardTypeContent}>
                  <div>{t("send-an-e-card")}</div>
                  <img
                    src={getAsset("/images/card-by-email.svg")}
                    width={104}
                    height={104}
                    alt=""
                  />
                </div>
              </RadioCard>

              <RadioCard value="post" variant="secondary">
                <div className={classes.cardTypeContent}>
                  <div>{t("send-a-card-by-post")}</div>
                  <img
                    src={getAsset("/images/card-by-post.svg")}
                    width={104}
                    height={104}
                    alt=""
                  />
                </div>
              </RadioCard>

              <RadioCard value="none" variant="secondary">
                <div className={classes.cardTypeContent}>
                  <div>{t("dont-send-a-card")}</div>
                  <img
                    src={getAsset("/images/dont-send-a-card.svg")}
                    width={104}
                    height={104}
                    alt=""
                  />
                </div>
              </RadioCard>
            </RadioGroup>
          </FormControl>

          {celebrationOptions.length > 1 && (
            <Field<CardSelectorType>
              name="celebrationType"
              serverErrorPaths={["card_selector.celebration_type"]}
              validate={() => {
                return z.string().min(1, {
                  message: t("errors.celebration-type-required") as string,
                });
              }}
              render={({
                value: celebrationType,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(config["card-selector-label-component-title"].i18n)}
                  error={errorMessage}
                  isValid={isValid}
                >
                  <Select
                    name="celebrationType"
                    value={celebrationType}
                    onChange={(ev) => handleChange(ev.target.value)}
                    onBlur={() => handleBlur()}
                    ref={ref as React.RefObject<HTMLSelectElement>}
                  >
                    <option value="">
                      {t(config["card-selector-label-select-celebration"].i18n)}
                    </option>

                    {celebrationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}

          {enableSelectCard && cardImages.length > 0 && (
            <Field<CardSelectorType, CardImage>
              name="cardImage"
              serverErrorPaths={["card_selector.card_image"]}
              validate={() => {
                return z.object({
                  src: z.string().min(1, {
                    message: t("errors.card-image-required") as string,
                  }),
                  alt: z.string(),
                });
              }}
              render={({ handleBlur, handleChange, value, errorMessage }) => (
                <FormControl
                  type="fieldset"
                  label={t(config["card-selector-label-select-card"].i18n)}
                  error={errorMessage}
                >
                  <RadioGroup
                    name="cardImage"
                    layout="auto-fit"
                    value={value?.src}
                    onChange={(event) => {
                      const src = event.target.value;

                      const image = cardImages.find(
                        (image) => image.file.src === src,
                      );

                      if (!image) return;

                      handleChange({
                        src: image.file.src as string,
                        alt: t(image.alt.i18n) as string,
                      });
                    }}
                  >
                    {cardImages.map((image: CelebrationImage) => (
                      <RadioImage
                        key={image.file.src}
                        value={image.file.src}
                        image={image.file.src}
                        alt={t(image.alt.i18n) as string}
                        onBlur={() => handleBlur()}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
          )}

          <Stack gap="md">
            <Heading as="h3" size="lg">
              {t(config["card-selector-label-donation-honouring"].i18n)}
            </Heading>

            <Field<CardSelectorType>
              name="honouringForename"
              serverErrorPaths={["card_selector.honouring_forename"]}
              validate={() => {
                return z.string().min(1, {
                  message: t("errors.honouring-forename-required") as string,
                });
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(config["card-selector-label-forename"].i18n)}
                  error={errorMessage}
                  isValid={isValid}
                >
                  <Input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    name="honouringForename"
                    value={value}
                    onChange={(ev) => handleChange(ev.target.value, ev)}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
              )}
            />

            <Field<CardSelectorType>
              name="honouringSurname"
              serverErrorPaths={["card_selector.honouring_surname"]}
              validate={() => {
                return z.string().min(1, {
                  message: t("errors.honouring-surname-required") as string,
                });
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(config["card-selector-label-surname"].i18n)}
                  error={errorMessage}
                  isValid={isValid}
                >
                  <Input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    name="honouringSurname"
                    value={value}
                    onChange={(ev) => handleChange(ev.target.value, ev)}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
              )}
            />
          </Stack>

          {enableSelectCard && (
            <>
              <Stack gap="md">
                <Heading as="h3" size="lg">
                  {t(config["card-selector-label-send-to"].i18n)}
                </Heading>
                <Field<CardSelectorType>
                  name="sendCardToForename"
                  serverErrorPaths={["card_selector.send_card_to_forename"]}
                  validate={() => {
                    return z.string().min(1, {
                      message: t(
                        "errors.send-card-to-forename-required",
                      ) as string,
                    });
                  }}
                  render={({
                    value,
                    handleChange,
                    handleBlur,
                    ref,
                    errorMessage,
                    isValid,
                  }) => (
                    <FormControl
                      label={t(config["card-selector-label-forename"].i18n)}
                      error={errorMessage}
                      isValid={isValid}
                    >
                      <Input
                        ref={ref as React.RefObject<HTMLInputElement>}
                        name="sendCardToForename"
                        value={value}
                        onChange={(ev) => handleChange(ev.target.value, ev)}
                        onBlur={() => handleBlur()}
                      />
                    </FormControl>
                  )}
                />

                <Field<CardSelectorType>
                  name="sendCardToSurname"
                  serverErrorPaths={["card_selector.send_card_to_surname"]}
                  validate={() => {
                    return z.string().min(1, {
                      message: t(
                        "errors.send-card-to-surname-required",
                      ) as string,
                    });
                  }}
                  render={({
                    value,
                    handleChange,
                    handleBlur,
                    ref,
                    errorMessage,
                    isValid,
                  }) => (
                    <FormControl
                      label={t(config["card-selector-label-surname"].i18n)}
                      error={errorMessage}
                      isValid={isValid}
                    >
                      <Input
                        ref={ref as React.RefObject<HTMLInputElement>}
                        name="sendCardToSurname"
                        value={value}
                        onChange={(ev) => handleChange(ev.target.value, ev)}
                        onBlur={() => handleBlur()}
                      />
                    </FormControl>
                  )}
                />

                {cardType === "post" && (
                  <CardSelectorAddressForm config={config} />
                )}

                {cardType === "e-card" && (
                  <>
                    <Field<CardSelectorType>
                      name="recipient.email"
                      serverErrorPaths={["card_selector.recipient.email"]}
                      validate={() => {
                        return z.string().email({
                          message: t("errors.email-invalid") as string,
                        });
                      }}
                      render={({
                        value,
                        handleChange,
                        handleBlur,
                        ref,
                        errorMessage,
                        isValid,
                      }) => (
                        <FormControl
                          label={t(
                            config["card-selector-label-recipient-email"].i18n,
                          )}
                          error={errorMessage}
                          isValid={isValid}
                        >
                          <Input
                            ref={ref as React.RefObject<HTMLInputElement>}
                            type="email"
                            name="recipientEmailAddress"
                            value={value}
                            onChange={(ev) => handleChange(ev.target.value, ev)}
                            onBlur={() => handleBlur()}
                          />
                        </FormControl>
                      )}
                    />

                    <Field<CardSelectorType>
                      name="recipient.confirmEmail"
                      dependencies={["recipient.email"]}
                      serverErrorPaths={[
                        "card_selector.recipient.confirm_email",
                      ]}
                      validate={(groupStateValues) => {
                        return z
                          .string()
                          .email({
                            message: t("errors.email-invalid") as string,
                          })
                          .refine(
                            (val) => val === groupStateValues.recipient.email,
                            t("errors.email-addresses-must-match") as string,
                          );
                      }}
                      render={({
                        value,
                        handleChange,
                        handleBlur,
                        ref,
                        errorMessage,
                        isValid,
                      }) => (
                        <FormControl
                          label={t(
                            config["card-selector-label-confirm-email"].i18n,
                          )}
                          error={errorMessage}
                          isValid={isValid}
                        >
                          <Input
                            ref={ref as React.RefObject<HTMLInputElement>}
                            type="email"
                            name="retypeRecipientEmailAddress"
                            value={value}
                            onChange={(ev) => handleChange(ev.target.value, ev)}
                            onBlur={() => handleBlur()}
                          />
                        </FormControl>
                      )}
                    />
                  </>
                )}
              </Stack>

              <Field<CardSelectorType>
                name="message"
                serverErrorPaths={["card_selector.message"]}
                validate={() => {
                  return z
                    .string()
                    .min(1, {
                      message: t("errors.message-required") as string,
                    })
                    .max(LIMIT_MESSAGE_CHARACTERS, {
                      message: t("errors.message-max-length", {
                        max: `${LIMIT_MESSAGE_CHARACTERS}`,
                      }) as string,
                    });
                }}
                render={({
                  value: message,
                  handleChange,
                  handleBlur,
                  ref,
                  errorMessage,
                  isValid,
                }) => (
                  <FormControl
                    label={t(config["card-selector-label-message"].i18n)}
                    error={errorMessage}
                    isValid={isValid}
                    helperText={
                      t("characters-remaining-count", {
                        count: `${Math.max(LIMIT_MESSAGE_CHARACTERS - message.length, 0)}`,
                      }) as string
                    }
                  >
                    <Textarea
                      name="personalMessage"
                      value={message}
                      onChange={(ev) => handleChange(ev.target.value)}
                      onBlur={() => handleBlur()}
                      ref={ref as React.RefObject<HTMLTextAreaElement>}
                      placeholder={t("personal-message-placeholder") as string}
                    />
                  </FormControl>
                )}
              />

              <Stack gap="md">
                {cardType === "post" && (
                  <div className={classes.htmlText}>
                    {t(config["card-selector-disclaimer-delivery"].i18n)}
                  </div>
                )}
                <div className={classes.htmlText}>
                  {t(config["card-selector-disclaimer-privacy"].i18n)}
                </div>
              </Stack>
            </>
          )}
        </Stack>
      </div>
    </ValidationGroupProvider>
  );
}
